import { Preview } from "@creatomate/preview";

import { ElementSource, Organization, Story, Subtitle, Template, TemplateType } from "chq";

import deleteElement from "./deleteElement";
import getDefaultSource from "./getDefaultSource";
import setCustomTemplate from "./setCustomTemplate";
import { loadSubtitle } from "../../sidebar/tabs/subtitle/utils";
import { GENERIC_ELEMENT, IMAGE_TYPE, VIDEO_TYPE } from "../constants";
import loadOrganizationFonts from "../loadOrganizationFonts";
import sourceToSnakeCase from "./sourceToSnakeCase";

const isHttpsUrl = (url: string | undefined): boolean => url !== undefined && url.startsWith("https://");
const isValidMediaType = (type: string | undefined): boolean => (
  type === VIDEO_TYPE || type === IMAGE_TYPE
);
const isNotLogo = (name: string | undefined): boolean => name === undefined || !name.toLowerCase().includes("logo");
const isNotAsset = (url: string): boolean => !url.toLowerCase().includes("culturehq-assets"); // intros or outros videos

const getMediaElements = (mediaType: string, preview: Preview) => {
  if (mediaType === VIDEO_TYPE) {
    return preview.state?.elements.filter(
      ({ source }) => isHttpsUrl(source.source)
        && isValidMediaType(source.type) && isNotLogo(source.name) && isNotAsset(source.source)
    );
  }

  return preview.state?.elements.filter(
    ({ source }) => source.source?.startsWith("https://") && source.type === "image"
  );
};

const syncFonts = async (organization: Organization, preview: Preview): Promise<void> => {
  const source = preview.getSource();
  const fonts = loadOrganizationFonts(organization);
  await preview.setSource({ ...source, fonts }, false);
};

const setTemplate = async (
  type: TemplateType,
  handleAutosaveDraft: (newData: Record<string, any>) => void,
  preview: Preview,
  organization: Organization,
  mediaType: string,
  template: Template,
  subtitles: Subtitle[],
  story?: Story,
  subtitlesOn?: boolean,
  burnSubtitles?: boolean
): Promise<void> => {
  const mediaElements = getMediaElements(mediaType, preview);
  if (template?.value && story) {
    await preview.loadTemplate(template.value);
    await setCustomTemplate(type, template, preview, organization, story, mediaElements);
    await syncFonts(organization, preview);
  } else if (template && story) {
    await preview.setSource(sourceToSnakeCase(template?.creatomateJson), false);
    await setCustomTemplate(type, template, preview, organization, story, mediaElements);
    await syncFonts(organization, preview);
  } else {
    await Promise.all((preview.state?.elements || []).map(async element => {
      await deleteElement(preview, element.source.id);
    }));
    await preview.setSource(await getDefaultSource({
      organization,
      preview,
      story,
      mediaElements
    }));
  }
  if (subtitles.length > 0 && subtitlesOn) {
    const subtitle = subtitles.find(subtitle => subtitle.default);
    if (!subtitle) return;

    const firstStory = template?.creatomateJson?.elements
      ?.filter((element: ElementSource) => element.name === GENERIC_ELEMENT.STORY)
      ?.reduce((min: ElementSource | null, current: ElementSource) => {
        if (!min || (current.time ?? 0) < (min.time ?? 0)) return current;

        return min;
      }, null as ElementSource | null);

    await loadSubtitle(
      handleAutosaveDraft,
      subtitles,
      subtitle,
      preview,
      preview.state?.elements || [],
      firstStory?.time ?? 0,
      organization.name,
      burnSubtitles,
      undefined,
      subtitle.language
    );
  }
};

export default setTemplate;
